import React, { useState, useEffect } from 'react';
import { PublicKey } from '@solana/web3.js';
import { supabase } from './supabaseClient';
import { motion } from 'framer-motion';
import {
  Window,
  WindowHeader,
  WindowContent,
  TextField,
  Button,
  Fieldset,
  Separator,
} from 'react95';
import './ArtistSubmission.css';

export default function ArtistSubmission() {
  const [form, setForm] = useState({
    name: '',
    description: '',
    wallet: '',
    file: null,
  });
  const [errors, setErrors] = useState({});
  const [artList, setArtList] = useState([]);
  const [votes, setVotes] = useState({});
  const [voterRecords, setVoterRecords] = useState({});
  const [selectedArt, setSelectedArt] = useState(null);
  const [voteArt, setVoteArt] = useState(null);
  const [voterWallet, setVoterWallet] = useState('');
  const [trackingArt, setTrackingArt] = useState(null);

  useEffect(() => {
    fetchArt();
    fetchVotes();
    const subscription = supabase
      .channel('realtime_votes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'votes' },
        fetchVotes
      )
      .subscribe();
    return () => supabase.removeChannel(subscription);
  }, []);

  async function fetchArt() {
    const { data } = await supabase.from('artworks').select('*');
    setArtList(data || []);
  }

  async function fetchVotes() {
    const { data } = await supabase
      .from('votes')
      .select('artwork_id, voter_wallet');
    const counts = {};
    const records = {};
    (data || []).forEach(({ artwork_id, voter_wallet }) => {
      counts[artwork_id] = (counts[artwork_id] || 0) + 1;
      records[artwork_id] = records[artwork_id] || [];
      records[artwork_id].push(voter_wallet);
    });
    setVotes(counts);
    setVoterRecords(records);
  }

  const validateName = (n) => /^[A-Za-z\s]{2,30}$/.test(n);
  const validateDescription = (d) => d.length <= 20;
  const validateSolanaWallet = (w) => {
    try {
      const pk = new PublicKey(w);
      return PublicKey.isOnCurve(pk);
    } catch {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((p) => ({ ...p, [name]: files ? files[0] : value }));
    setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, description, wallet, file } = form;
    const errs = {};
    if (!validateName(name)) errs.name = 'Only letters and spaces.';
    if (!validateDescription(description))
      errs.description = 'Max 20 characters.';
    if (!validateSolanaWallet(wallet)) errs.wallet = 'Invalid Solana address.';
    if (!file) errs.file = 'Image required.';
    const { data: ex } = await supabase
      .from('artworks')
      .select('id')
      .eq('wallet', wallet);
    if (ex?.length > 0) errs.wallet = 'Wallet already submitted.';
    if (Object.keys(errs).length) return setErrors(errs);

    const ext = file.name.split('.').pop();
    const filename = `${Date.now()}.${ext}`;
    await supabase.storage.from('artworks').upload(filename, file);
    const publicUrl = supabase.storage.from('artworks').getPublicUrl(filename)
      .data.publicUrl;
    await supabase
      .from('artworks')
      .insert([{ title: name, description, wallet, image_url: publicUrl }]);
    setForm({ name: '', description: '', wallet: '', file: null });
    fetchArt();
  };

  const handleVoteSubmit = async () => {
    if (!validateSolanaWallet(voterWallet)) {
      alert('❌ Invalid wallet. Use a real Solana address.');
      return;
    }
    const { error } = await supabase
      .from('votes')
      .insert([{ artwork_id: voteArt.id, voter_wallet: voterWallet }]);
    if (error) {
      alert(error.code === '23505' ? 'You already voted.' : 'Vote error.');
    } else {
      alert('✅ Vote recorded!');
      fetchVotes();
    }
    setVoterWallet('');
    setVoteArt(null);
  };

  const leaderboard = artList
    .map((a) => ({ ...a, voteCount: votes[a.id] || 0 }))
    .sort((a, b) => b.voteCount - a.voteCount);

  return (
    <div className='containerxz'>
      {/* Submission Form */}
      <Window className='form-section'>
        <WindowHeader>submit-art.exe</WindowHeader>
        <WindowContent>
          <form onSubmit={handleSubmit} className='submission-form'>
            <Fieldset label='Artist Name'>
              <TextField
                fullWidth
                name='name'
                value={form.name}
                onChange={handleChange}
              />
              {errors.name && <div className='error'>{errors.name}</div>}
            </Fieldset>
            <Fieldset label='Description (max 20 chars)'>
              <TextField
                fullWidth
                name='description'
                value={form.description}
                onChange={handleChange}
              />
              {errors.description && (
                <div className='error'>{errors.description}</div>
              )}
            </Fieldset>
            <Fieldset label='Solana Wallet Address'>
              <TextField
                fullWidth
                name='wallet'
                value={form.wallet}
                onChange={handleChange}
              />
              {errors.wallet && <div className='error'>{errors.wallet}</div>}
            </Fieldset>
            <Fieldset label='Upload Image'>
              <input
                type='file'
                accept='image/*'
                name='file'
                onChange={handleChange}
              />
              {errors.file && <div className='error'>{errors.file}</div>}
            </Fieldset>
            <Separator style={{ margin: '20px 0' }} />
            <Button type='submit'>🎨 Submit Artwork</Button>
          </form>
        </WindowContent>
      </Window>

      {/* Gallery */}
      <Window className='list-section'>
        <WindowHeader>gallery.exe</WindowHeader>
        <WindowContent>
          <motion.div
            className='listGrid'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {leaderboard.map((art, idx) => (
              <motion.div
                className='listItem'
                key={art.id}
                whileHover={{ scale: 1.05 }}
              >
                <div className='rank-badge'>#{idx + 1}</div>
                <img
                  src={art.image_url}
                  alt={art.title}
                  onClick={() => setSelectedArt(art)}
                />
                <div className='tipTitle'>{art.title}</div>
                <div className='tipDesc'>{art.description}</div>
                <div className='voteCount'>💖 {art.voteCount} votes</div>
                <Button size='sm' onClick={() => setVoteArt(art)}>
                  👍 Vote
                </Button>
                <Button
                  size='sm'
                  onClick={() => {
                    navigator.clipboard.writeText(art.wallet);
                    alert('Artist wallet copied!');
                  }}
                >
                  💸 Tip
                </Button>
                <Button size='sm' onClick={() => setTrackingArt(art)}>
                  🔍 Track Votes
                </Button>
              </motion.div>
            ))}
          </motion.div>
        </WindowContent>
      </Window>

      {/* Vote Modal with Quick Test Wallets */}
      {voteArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Vote for "{voteArt.title}"
              <Button size='sm' onClick={() => setVoteArt(null)}>
                x
              </Button>
            </WindowHeader>
            <WindowContent>
              <Fieldset label='Your Solana Wallet'>
                <TextField
                  fullWidth
                  value={voterWallet}
                  onChange={(e) => setVoterWallet(e.target.value)}
                />
              </Fieldset>

              <Button onClick={handleVoteSubmit}>✅ Vote</Button>
            </WindowContent>
          </Window>
        </div>
      )}

      {/* Vote Tracker Modal */}
      {trackingArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Vote Tracker — {trackingArt.title}
              <Button size='sm' onClick={() => setTrackingArt(null)}>
                x
              </Button>
            </WindowHeader>
            <WindowContent>
              <ul>
                {(voterRecords[trackingArt.id] || []).map((w, i) => (
                  <li key={i}>
                    <a
                      href={`https://solscan.io/account/${w}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {w.slice(0, 6)}...{w.slice(-4)}
                    </a>
                  </li>
                ))}
                {(!voterRecords[trackingArt.id] ||
                  voterRecords[trackingArt.id].length === 0) && (
                  <p>No votes yet.</p>
                )}
              </ul>
            </WindowContent>
          </Window>
        </div>
      )}

      {/* Leaderboard Panel */}
      <Window className='leaderboard-section'>
        <WindowHeader>leaderboard.exe</WindowHeader>
        <WindowContent>
          <ol>
            {leaderboard.map((a, i) => (
              <li key={a.id}>
                #{i + 1} – {a.title} ({a.voteCount} votes)
              </li>
            ))}
          </ol>
        </WindowContent>
      </Window>

      {/* Image preview */}
      {selectedArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Artwork
              <Button
                size='sm'
                className='displayendcross'
                onClick={() => setSelectedArt(null)}
              >
                x
              </Button>
            </WindowHeader>
            <WindowContent>
              <img
                src={selectedArt.image_url}
                alt={selectedArt.title}
                className='modal-image'
              />
              <p>{selectedArt.description}</p>
            </WindowContent>
          </Window>
        </div>
      )}
    </div>
  );
}
